// ==========================================================================
// components/PhotoEditor.tsx — crop, turn and give a photo a look before it's
// kept (FF-060, 10 Oct 2026).
//   James: "a way to edit, crop and make it clean and easy for the user" and
//   "more fun like instagram with cropping and some filters".
//   Full screen, Instagram-style: the frame stays put and the photo moves
//   under it (drag, pinch or the slider to zoom). Shapes: as taken, square,
//   4:5, 3:4, 16:9. A quarter turn. Nine looks and a light slider.
//   Everything happens on the phone; the edited copy (JPEG, 2048 px at most)
//   is what gets uploaded. "Use as is" keeps the original.
// ==========================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPE } from "react";
import { Crop, Loader2, RotateCw, Sparkles, Sun, X } from "lucide-react";
import { LOOKS, loadToCanvas, rotate90, thumbOf, withLook, type Look } from "@/lib/photoFilters";

type Shape = { key: string; label: string; ratio: number | null };
const SHAPES: Shape[] = [
  { key: "orig", label: "As taken", ratio: null },
  { key: "sq", label: "Square", ratio: 1 },
  { key: "45", label: "4:5", ratio: 4 / 5 },
  { key: "34", label: "3:4", ratio: 3 / 4 },
  { key: "169", label: "16:9", ratio: 16 / 9 },
];

const toUrl = (c: HTMLCanvasElement, q = 0.85) => new Promise<string>((res) => c.toBlob((b) => res(b ? URL.createObjectURL(b) : c.toDataURL()), "image/jpeg", q));

export default function PhotoEditor({ file, index, total, accent, onDone, onSkip, onCancel }: {
  file: File; index: number; total: number; accent: string;
  onDone: (f: File) => void; onSkip: () => void; onCancel: () => void;
}) {
  const [base, setBase] = useState<HTMLCanvasElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [look, setLook] = useState<Look>(LOOKS[0]);
  const [light, setLight] = useState(0);
  const [shape, setShape] = useState<Shape>(SHAPES[0]);
  const [tab, setTab] = useState<"crop" | "look">("crop");
  const [z, setZ] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [preview, setPreview] = useState<string | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 360, h: 480 });

  // load the photo
  useEffect(() => {
    let live = true;
    setBase(null); setErr(null); setLook(LOOKS[0]); setLight(0); setShape(SHAPES[0]); setZ(1); setOff({ x: 0, y: 0 }); setTab("crop");
    loadToCanvas(file).then((c) => { if (live) setBase(c); }).catch(() => live && setErr("This photo couldn't be opened for editing. You can still use it as is."));
    return () => { live = false; };
  }, [file]);

  // a lighter copy for the screen, and the look thumbnails
  const small = useMemo(() => {
    if (!base) return null;
    const k = Math.min(1, 1200 / Math.max(base.width, base.height));
    if (k === 1) return base;
    const c = document.createElement("canvas"); c.width = Math.round(base.width * k); c.height = Math.round(base.height * k);
    c.getContext("2d")!.drawImage(base, 0, 0, c.width, c.height); return c;
  }, [base]);
  useEffect(() => {
    if (!small) return;
    let live = true;
    const t = setTimeout(() => { void toUrl(withLook(small, look, light)).then((u) => { if (live) setPreview((old) => { if (old?.startsWith("blob:")) URL.revokeObjectURL(old); return u; }); }); }, 0);
    return () => { live = false; clearTimeout(t); };
  }, [small, look, light]);
  useEffect(() => {
    if (!small) return;
    const th = thumbOf(small, 140); const out: Record<string, string> = {};
    for (const l of LOOKS) out[l.key] = withLook(th, l).toDataURL("image/jpeg", 0.8);
    setThumbs(out);
  }, [small]);

  // the stage size
  useEffect(() => {
    const el = stage.current; if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  // geometry, in screen pixels
  const iw = base?.width ?? 1, ih = base?.height ?? 1;
  const ratio = shape.ratio ?? iw / ih;
  const room = { w: box.w - 32, h: box.h - 32 };
  const FW = Math.max(1, Math.min(room.w, room.h * ratio)), FH = Math.max(1, FW / ratio);
  const s0 = Math.max(FW / iw, FH / ih);
  const dw = iw * s0 * z, dh = ih * s0 * z;
  const clampOff = useCallback((o: { x: number; y: number }, zz = z) => {
    const mx = Math.max(0, (iw * s0 * zz - FW) / 2), my = Math.max(0, (ih * s0 * zz - FH) / 2);
    return { x: Math.max(-mx, Math.min(mx, o.x)), y: Math.max(-my, Math.min(my, o.y)) };
  }, [iw, ih, s0, FW, FH, z]);
  useEffect(() => { setOff((o) => clampOff(o)); }, [clampOff]);

  // drag and pinch
  const pts = useRef(new Map<number, { x: number; y: number }>());
  const g = useRef<{ x: number; y: number; ox: number; oy: number; d?: number; z?: number } | null>(null);
  const down = (e: RPE) => {
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = [...pts.current.values()];
    g.current = p.length === 2 ? { x: 0, y: 0, ox: off.x, oy: off.y, d: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y), z } : { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
    setDragging(true);
  };
  const move = (e: RPE) => {
    if (!pts.current.has(e.pointerId) || !g.current) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = [...pts.current.values()];
    if (p.length === 2 && g.current.d) {
      const nz = Math.min(4, Math.max(1, (g.current.z ?? 1) * Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) / g.current.d));
      setZ(nz); setOff((o) => clampOff(o, nz));
    } else if (p.length === 1) {
      setOff(clampOff({ x: g.current.ox + e.clientX - g.current.x, y: g.current.oy + e.clientY - g.current.y }));
    }
  };
  const up = (e: RPE) => {
    pts.current.delete(e.pointerId);
    const p = [...pts.current.values()];
    g.current = p.length === 1 ? { x: p[0].x, y: p[0].y, ox: off.x, oy: off.y } : null;
    if (!p.length) setDragging(false);
  };
  const wheel = (e: React.WheelEvent) => { const nz = Math.min(4, Math.max(1, z * (e.deltaY < 0 ? 1.08 : 1 / 1.08))); setZ(nz); setOff((o) => clampOff(o, nz)); };

  const turn = () => { if (!base) return; setBase(rotate90(base)); setZ(1); setOff({ x: 0, y: 0 }); };

  const done = async () => {
    if (!base) return;
    setBusy(true);
    try {
      const full = withLook(base, look, light);
      const k = 1 / (s0 * z);
      const sx = iw / 2 + (-FW / 2 - off.x) * k, sy = ih / 2 + (-FH / 2 - off.y) * k;
      const cw = Math.round(FW * k), ch = Math.round(FH * k);
      const out = document.createElement("canvas"); out.width = Math.max(1, cw); out.height = Math.max(1, ch);
      out.getContext("2d")!.drawImage(full, sx, sy, FW * k, FH * k, 0, 0, out.width, out.height);
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, "image/jpeg", 0.9));
      if (!blob) throw new Error("no blob");
      onDone(new File([blob], file.name.replace(/\.[^.]+$/, "") + "-edited.jpg", { type: "image/jpeg" }));
    } catch {
      setErr("Couldn't save the edit on this device. You can use the photo as is.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#111] text-white" role="dialog" aria-label="Edit photo">
      <div className="flex items-center justify-between px-4 pb-2 pt-[calc(env(safe-area-inset-top)+12px)]">
        <button type="button" onClick={onCancel} className="flex items-center gap-1 rounded-full px-2 py-1.5 text-sm text-white/80 hover:text-white"><X className="h-5 w-5" />Cancel</button>
        <div className="text-sm font-semibold">{total > 1 ? `Photo ${index + 1} of ${total}` : "Edit photo"}</div>
        <button type="button" onClick={done} disabled={!base || busy} className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: accent }}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{index + 1 < total ? "Next" : "Done"}
        </button>
      </div>

      <div ref={stage} className="relative min-h-0 flex-1 touch-none select-none overflow-hidden" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={wheel}>
        {!base && !err && <div className="absolute inset-0 flex items-center justify-center text-white/60"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {err && <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-sm text-white/80">{err}</div>}
        {base && preview && <>
          <img src={preview} alt="" draggable={false} className="pointer-events-none absolute max-w-none"
            style={{ width: dw, height: dh, left: box.w / 2 - dw / 2 + off.x, top: box.h / 2 - dh / 2 + off.y }} />
          {/* the frame: everything outside it is dimmed */}
          <div className="pointer-events-none absolute rounded-[3px]" style={{ width: FW, height: FH, left: (box.w - FW) / 2, top: (box.h - FH) / 2, boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)", outline: "1.5px solid rgba(255,255,255,0.85)" }}>
            {dragging && <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(to right, transparent 33%, rgba(255,255,255,.45) 33%, rgba(255,255,255,.45) calc(33% + 1px), transparent calc(33% + 1px), transparent 66%, rgba(255,255,255,.45) 66%, rgba(255,255,255,.45) calc(66% + 1px), transparent calc(66% + 1px)), linear-gradient(to bottom, transparent 33%, rgba(255,255,255,.45) 33%, rgba(255,255,255,.45) calc(33% + 1px), transparent calc(33% + 1px), transparent 66%, rgba(255,255,255,.45) 66%, rgba(255,255,255,.45) calc(66% + 1px), transparent calc(66% + 1px))" }} />}
          </div>
        </>}
      </div>

      <div className="space-y-3 px-4 pb-[calc(env(safe-area-inset-bottom)+14px)] pt-3">
        {tab === "crop" ? (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
              {SHAPES.map((s) => <button key={s.key} type="button" onClick={() => { setShape(s); setZ(1); setOff({ x: 0, y: 0 }); }}
                className={`shrink-0 rounded-full px-3.5 py-2 text-sm ${shape.key === s.key ? "bg-white font-semibold text-black" : "bg-white/10 text-white/80"}`}>{s.label}</button>)}
              <button type="button" onClick={turn} className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-2 text-sm text-white/80"><RotateCw className="h-4 w-4" />Turn</button>
            </div>
            <label className="flex items-center gap-3 text-xs text-white/60">Zoom
              <input type="range" min={1} max={4} step={0.01} value={z} onChange={(e) => { const nz = Number(e.target.value); setZ(nz); setOff((o) => clampOff(o, nz)); }} className="flex-1" style={{ accentColor: accent }} />
            </label>
          </>
        ) : (
          <>
            <div className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none]">
              {LOOKS.map((l) => <button key={l.key} type="button" onClick={() => setLook(l)} className="flex shrink-0 flex-col items-center gap-1.5">
                <span className={`block h-16 w-16 overflow-hidden rounded-xl ${look.key === l.key ? "ring-2 ring-white" : "opacity-80"}`}>{thumbs[l.key] && <img src={thumbs[l.key]} alt="" className="h-full w-full object-cover" />}</span>
                <span className={`text-[11px] ${look.key === l.key ? "font-semibold text-white" : "text-white/60"}`}>{l.label}</span>
              </button>)}
            </div>
            <label className="flex items-center gap-3 text-xs text-white/60"><Sun className="h-4 w-4" />
              <input type="range" min={-0.3} max={0.3} step={0.01} value={light} onChange={(e) => setLight(Number(e.target.value))} className="flex-1" style={{ accentColor: accent }} />
            </label>
          </>
        )}
        <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-3">
          <div className="flex rounded-full bg-white/10 p-1">
            <button type="button" onClick={() => setTab("crop")} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm ${tab === "crop" ? "bg-white font-semibold text-black" : "text-white/70"}`}><Crop className="h-4 w-4" />Crop</button>
            <button type="button" onClick={() => setTab("look")} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm ${tab === "look" ? "bg-white font-semibold text-black" : "text-white/70"}`}><Sparkles className="h-4 w-4" />Looks</button>
          </div>
          <button type="button" onClick={onSkip} className="rounded-full px-3 py-1.5 text-sm text-white/70 hover:text-white">Use as is</button>
        </div>
      </div>
    </div>
  );
}
